// src/apiFetch.js

// If you set REACT_APP_API_BASE, we'll send requests to that absolute base (e.g. "http://69.75.224.54:5050").
// Otherwise we use RELATIVE URLs ("/api/...") so CRA dev-server proxy works.
const envBase = (process.env.REACT_APP_API_BASE || "").replace(/\/+$/, "");
const API_BASE = envBase || ""; // ✅ relative by default (enables CRA proxy)

// ✅ UI-only (no backend) mode toggle
const UI_ONLY = String(process.env.REACT_APP_UI_ONLY || "").toLowerCase() === "true";


/* ---- Legacy token helpers (deprecated) ----
   We no longer store JWT in localStorage. Auth is via httpOnly cookie.
   These helpers remain ONLY to auto-clean old installs.
*/
export function getAuthToken() {
  const t = localStorage.getItem("token");
  if (t) localStorage.removeItem("token"); // auto-clean legacy token
  return null;
}

export function setAuthToken(_token) {
  // no-op (cookies now)
}

export function clearAuth() {
  localStorage.removeItem("token");
}

// ---- Optional: pricing override header (DEV/ADMIN only) ----
// ✅ Default: do NOT send any pricing override from normal clients.
let PRICING_OVERRIDE = null; // "off" | "on" | null
export function setPricingOverride(value /* "off" | "on" | null */) {
  PRICING_OVERRIDE = value == null ? null : String(value).toLowerCase();
}

// Small helper to safely join base + path
function joinUrl(base, path) {
  if (!base) return path; // ✅ relative (CRA proxy)
  return `${base.replace(/\/+$/, "")}${path}`;
}

// Resolve a server-relative media path (e.g. "/component-media/220-207SU-PIC-01.png")
// against API_BASE so <img>/<video> load from the API origin, not the frontend origin.
// Absolute URLs (http, https, data:) and empty values are returned unchanged.
export function mediaUrl(path) {
  const p = String(path || "");
  if (!p || /^(https?:|data:|blob:)/i.test(p)) return p;
  return joinUrl(API_BASE, p);
}

function readCookie(name) {
  if (typeof document === "undefined") return "";
  const m = document.cookie.match(new RegExp("(^|; )" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[2]) : "";
}

function readCachedUser() {
  try {
    const raw = localStorage.getItem("cbc_user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

// Acting-customer lookup runs on every request. getItem is cheap; JSON.parse is
// what we save — the parsed value is cached keyed by the raw string, so any
// writer (ActAsCustomer, CBCOrderingLayout, other tabs) is picked up automatically.
let actingRawCache = undefined;
let actingParsedCache = null;
function readActingCustomer() {
  const raw = localStorage.getItem("cbc_acting_customer");
  if (raw === actingRawCache) return actingParsedCache;
  actingRawCache = raw;
  try {
    actingParsedCache = raw ? JSON.parse(raw) : null;
  } catch {
    actingParsedCache = null;
  }
  return actingParsedCache;
}

// ✅ UI-only mock router
// UI-only item notes, kept for the session so add / edit / delete can be exercised
const mockItemNotes = [
  { id: 1, itemNo: "982-301-9", kind: "improvement", text: "Wants the slat edge sealed better — chips when cut on site.", customerNo: "ACME", customerName: "ACME BLINDS", createdBy: "someone@cbc.com", createdByName: "Other Rep", createdAt: "2026-10-15T18:00:00Z", updatedAt: null, canEdit: false },
];
let mockNoteSeq = 2;
// Minimum stock rows (UI-only).
const mockMinStock = [
  { itemNo: "220-202M", minQty: 10, requestedQty: 20, status: "pending", requestedByName: "Sales Rep", requestedAt: "2026-10-23T17:00:00Z", requestNote: "Dealer stocks this" },
];

// UI-only sticky notes / sales plans / calendar, kept for the session
const mockSticky = [];
let mockStickySeq = 1;
const mockPlans = [];
const mockGrowthFeedback = [];
let mockPlanSeq = 1;
const mockCalEvents = (() => {
  const d = new Date(); d.setHours(10, 0, 0, 0);
  const e = new Date(d); e.setHours(11, 0, 0, 0);
  return [{ id: "ev1", title: "Team meeting", start: d.toISOString(), end: e.toISOString(), allDay: false, location: "", link: "" }];
})();

function mockApi(path, { method = "GET", body } = {}) {
  const m = String(method || "GET").toUpperCase();
  const user = readCachedUser();

  // Keep shape consistent with server
  if (path === "/api/csrf") return { ok: true };
  if (path === "/api/me") return { ok: true, user: user || null };

  if (path === "/api/login" && m === "POST") return { ok: true };
  if (path === "/api/logout" && m === "POST") return { ok: true };
  if (path === "/api/set-password" && m === "POST") return { ok: true };
  if (path === "/api/health") return { ok: true };

  // Minimum stock (UI-only)
  if (path.startsWith("/api/min-stock")) {
    const stockOf = (r) => {
      const lead = 12, wk = 1.66, onHand = 2, inbound = 5;
      const projected = Math.round((onHand + inbound - wk * lead) * 10) / 10;
      const short = r.minQty > 0 ? Math.max(0, Math.ceil(r.minQty - projected)) : 0;
      return { inPlan: true, description: `MOCK ITEM ${r.itemNo}`, onHand, allocated: 0, onOrder: inbound, inbound, weeklyDemand: wk, leadWeeks: lead,
        leadDemand: wk * lead, projected, short, stockStatus: r.minQty > 0 ? (short > 0 ? "short" : "ok") : "no-min", suggested: 13 };
    };
    const canApprove = (user?.pages || []).includes("purchasing") || (user?.pages || []).includes("admin");
    if (path.startsWith("/api/min-stock/search")) {
      const q = new URLSearchParams(path.split("?")[1] || "").get("q") || "";
      return { ok: true, items: ["220-202M", "220-202L", "630-301-345-720"].filter((i) => i.startsWith(q.toUpperCase())).map((itemNo) => ({ itemNo, description: `MOCK ITEM ${itemNo}` })) };
    }
    const put = (itemNo, qty) => {
      const n = Number(qty);
      let r = mockMinStock.find((x) => x.itemNo === itemNo);
      if (!r) { r = { itemNo, minQty: null, requestedQty: null, status: "pending" }; mockMinStock.push(r); }
      if (canApprove) Object.assign(r, { minQty: n > 0 ? n : null, requestedQty: null, status: "approved", reviewedByName: user?.name || "Me", reviewedAt: new Date().toISOString() });
      else Object.assign(r, { requestedQty: n, status: "pending", requestedByName: user?.name || "Me", requestedAt: new Date().toISOString() });
      return r;
    };
    const itemM = path.match(/^\/api\/min-stock\/item\/([^/?]+)(?:\/(approve|reject))?/);
    if (itemM) {
      const itemNo = decodeURIComponent(itemM[1]).toUpperCase();
      const r = mockMinStock.find((x) => x.itemNo === itemNo) || null;
      if (itemM[2] === "approve" && r) Object.assign(r, { minQty: r.requestedQty || r.minQty, requestedQty: null, status: "approved" });
      if (itemM[2] === "reject" && r) Object.assign(r, { requestedQty: null, status: r.minQty ? "approved" : "rejected" });
      if (m === "PUT") return { ok: true, row: put(itemNo, body?.qty) };
      return { ok: true, itemNo, row: r, stock: stockOf(r || { itemNo, minQty: 0 }), canApprove };
    }
    if (path.startsWith("/api/min-stock/bulk")) {
      const lines = String(body?.text || "").split(/\r?\n/).map((l) => l.trim().split(/\t|,|\s+/)).filter((p) => p.length >= 2 && /^\d+$/.test(p[p.length - 1]));
      const preview = lines.map((p) => ({ itemNo: p[0].toUpperCase(), qty: Number(p[p.length - 1]), description: `MOCK ITEM ${p[0]}`, current: null, action: "new" }));
      if (body?.preview) return { ok: true, preview, errors: [], willApprove: canApprove };
      preview.forEach((p) => put(p.itemNo, p.qty));
      return { ok: true, saved: preview.length, unchanged: 0, unknown: [], errors: [], approved: canApprove };
    }
    if (path.startsWith("/api/min-stock/send-weekly")) return { ok: true, result: { sent: true, to: ["buyer@cbc.com"] } };
    const qs = new URLSearchParams(path.split("?")[1] || "");
    const all = mockMinStock.map((r) => ({ ...r, ...stockOf(r) }));
    const status = qs.get("status") || "all";
    const rows = all.filter((r) => status === "all" || (status === "pending" ? r.status === "pending" : r.stockStatus === "short"));
    return { ok: true, rows, canApprove, counts: { all: all.length, pending: all.filter((r) => r.status === "pending").length, short: all.filter((r) => r.stockStatus === "short").length } };
  }

  // Item notes (UI-only)
  if (path.startsWith("/api/cs/item-notes/counts")) {
    const counts = {};
    for (const n of mockItemNotes) {
      const c = counts[n.itemNo] || (counts[n.itemNo] = { notes: 0, improvements: 0 });
      c.notes++;
      if (n.kind === "improvement") c.improvements++;
    }
    return { ok: true, counts };
  }
  if (/^\/api\/cs\/items\/[^/]+\/notes/.test(path)) {
    const itemNo = decodeURIComponent(path.split("/")[4]).toUpperCase();
    if (m === "POST") {
      const note = { id: mockNoteSeq++, itemNo, kind: body?.kind || "note", text: String(body?.text || "").trim(), customerNo: body?.customerNo || null, customerName: body?.customerName || null,
        createdBy: user?.email || "me@cbc.com", createdByName: user?.name || "Me", createdAt: new Date().toISOString(), updatedAt: null, canEdit: true };
      mockItemNotes.unshift(note);
      return { ok: true, note };
    }
    return { ok: true, itemNo, notes: mockItemNotes.filter((n) => n.itemNo === itemNo) };
  }
  if (path === "/api/cs/item-notes" || path.startsWith("/api/cs/item-notes?")) {
    const qs = new URLSearchParams(path.split("?")[1] || "");
    const kind = qs.get("kind") || "improvement";
    const q = (qs.get("q") || "").toLowerCase();
    const notes = mockItemNotes
      .filter((n) => (kind === "all" || n.kind === kind) && (!q || `${n.text} ${n.itemNo} ${n.customerName || ""}`.toLowerCase().includes(q)))
      .map((n) => ({ ...n, description: `MOCK ITEM ${n.itemNo}`, prodCat: "FS0" }));
    return { ok: true, kind, notes, truncated: false };
  }
  if (path.startsWith("/api/cs/item-notes/")) {
    const id = Number(path.split("/")[4]);
    const i = mockItemNotes.findIndex((n) => n.id === id);
    if (i < 0) return { ok: false, error: "Note not found" };
    if (m === "DELETE") { mockItemNotes.splice(i, 1); return { ok: true, deleted: true }; }
    Object.assign(mockItemNotes[i], { kind: body?.kind, text: body?.text, customerNo: body?.customerNo || null, customerName: body?.customerName || null, updatedAt: new Date().toISOString() });
    return { ok: true, note: mockItemNotes[i] };
  }

  // Sticky notes (UI-only)
  if (path.startsWith("/api/me/sticky-notes")) {
    const rest = path.replace("/api/me/sticky-notes", "").split("?")[0];
    const now = Date.now();
    if (rest === "/due") {
      const open = mockSticky.filter((n) => !n.done && !n.remindedAt && n.remindAt);
      return { ok: true, due: open.filter((n) => new Date(n.remindAt).getTime() <= now), upcoming: open.filter((n) => { const t = new Date(n.remindAt).getTime(); return t > now && t <= now + 120000; }) };
    }
    if (rest === "" && m === "POST") {
      const note = { id: mockStickySeq++, text: body?.text || "", color: body?.color || "yellow", links: body?.links || [], remindAt: body?.remindAt || null, remindedAt: null, done: false, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
      mockSticky.unshift(note);
      return { ok: true, note };
    }
    if (rest === "") {
      const status = (path.split("status=")[1] || "open").split("&")[0];
      return { ok: true, notes: mockSticky.filter((n) => (status === "done" ? n.done : status === "all" ? true : !n.done)) };
    }
    const [, idStr, action] = rest.split("/");
    const n = mockSticky.find((x) => x.id === Number(idStr));
    if (!n) return { ok: false, error: "Note not found" };
    if (m === "DELETE") { mockSticky.splice(mockSticky.indexOf(n), 1); return { ok: true }; }
    if (action === "ack") { n.remindedAt = new Date().toISOString(); return { ok: true }; }
    if (action === "snooze") { n.remindAt = body?.until || new Date(now + (Number(body?.minutes) || 10) * 60000).toISOString(); n.remindedAt = null; return { ok: true, note: n }; }
    if (body?.remindAt !== undefined) n.remindedAt = null;
    Object.assign(n, Object.fromEntries(Object.entries(body || {}).filter(([k]) => ["text", "color", "links", "remindAt", "done"].includes(k))), { updatedAt: new Date().toISOString() });
    return { ok: true, note: n };
  }

  // Google Calendar (UI-only): pretend connected with one event today
  if (path === "/api/me/google/status") return { ok: true, configured: true, connected: true, googleEmail: "me@gmail.com" };
  if (path.startsWith("/api/me/google/auth-url")) return { ok: false, error: "Not available in UI-only mode" };
  if (path.startsWith("/api/me/calendar/events")) {
    if (m === "POST") {
      const ev = { id: `ev${mockCalEvents.length + 1}`, title: body?.title, start: body?.start, end: body?.end, allDay: false, location: body?.location || "", link: "" };
      mockCalEvents.push(ev);
      return { ok: true, event: ev };
    }
    return { ok: true, events: mockCalEvents };
  }

  // One mock account so Customer 360 (and its Sales plan tab) opens in UI-only mode
  {
    const MOCK_ACCT = "11111111-2222-3333-4444-555555555555";
    if (path.startsWith("/api/admin/accounts?")) {
      return { ok: true, accounts: [{ AccountId: MOCK_ACCT, Name: "Mock Account", AccountNumber: "MOCK1", EMailAddress1: "buyer@example.com", contactCount: 1 }] };
    }
    if (path === `/api/admin/accounts/${MOCK_ACCT}`) {
      return { ok: true, account: { AccountId: MOCK_ACCT, Name: "Mock Account", AccountNumber: "MOCK1", EMailAddress1: "buyer@example.com", Telephone1: "555-0100", AddressStreet1: "1 Main St", AddressCity: "Los Angeles", AddressState: "CA", AddressZip: "90001", IsActive: true, IsCustomer: true },
        contacts: [{ ContactId: "c1", Name: "Pat Buyer", Email: "pat@example.com", Mobile: "555-0111" }] };
    }
    if (path.startsWith(`/api/admin/accounts/${MOCK_ACCT}/notes`)) return { ok: true, notes: [] };
    if (path.startsWith(`/api/admin/accounts/${MOCK_ACCT}/closure`)) return { ok: true, closed: false };

    // Product cost (Cost & margin tab, tier pricing) — figures from item 220-113-205
    const fx = { sheetNtdPerUsd: 30.5, landedFactor: 1.12, current: null, currentSetBy: "", currentSetAt: null, currentNote: "" };
    const cost = { hasCost: true, costBasis: "sheet", sheet: { sellingUnit: "PKG", qtyPerCount: 100 }, twCostNtd: 1510, markupNtd: 2076.25,
      fobUsd: 68.0738, landedUsd: 76.2426, sheetFobUsd: 68.0738, sheetLandedUsd: 76.2426, marginVsLandedPct: 41, marginVsFobPct: 47.4,
      avgPaidMarginVsLandedPct: 40.1, avgPaidMarginVsFobPct: 46.6, flags: ["unit-mismatch"],
      units: { priceUnit: "BX", countUnit: "CT", costFactor: 100, custom: false, ruleId: null, pattern: null, fallback: false, steps: ["1 PKG = 100 CT (sheet QNTY per Count)"], missing: [], explain: "Cost per BX = cost per count × 100", alt: [], lengthIn: null, pack: 100 } };
    const vane = { itemNo: "122-205-1920", description: "SMOOTH CURVED 27G,WHITE", macolaUom: "PC", prodCat: "PVC", prodCatLabel: "PVC curved vanes", categoryGroups: ["Vertical blinds"],
      qty12m: 630, revenue12m: 1247, avgPaidUsd: 1.98, lastPaidUsd: 1.98, lastInvoiced: "2026-09-12", basePriceUsd: 3.44, customerPriceUsd: 1.98,
      rule: { kind: "Customer type", basis: "D", breakMinQty: 0, breakValue: 42.38 },
      cost: { hasCost: true, costBasis: "sheet", sheet: { category: "VANE", sellingUnit: "PC", qtyPerCount: 192, packing: "100PC/BX" }, twCostNtd: 288, markupNtd: 378.1,
        fobUsd: 12.39, landedUsd: 13.88, sheetFobUsd: 12.39, sheetLandedUsd: 13.88, marginVsLandedPct: -601.5, marginVsFobPct: -526.3, avgPaidMarginVsLandedPct: -601.5, flags: ["length-item"],
        units: { priceUnit: "PC", countUnit: "CT", costFactor: 192, custom: false, ruleId: null, pattern: null, fallback: false, steps: ["1 PC = 192 CT (sheet QNTY per Count)"], missing: [], explain: "Cost per PC = cost per count × 192", alt: [], lengthIn: 192, pack: 100 } } };
    const item = { itemNo: "220-113-205", description: "HP BOX BKT,WHITE SATIN", macolaUom: "BX", qty12m: 14, revenue12m: 1772.4, avgPaidUsd: 126.6,
      lastPaidUsd: 129.3, lastInvoiced: "2026-09-30", basePriceUsd: 226.4, customerPriceUsd: 129.3,
      rule: { kind: "Customer type", basis: "D", breakMinQty: 0, breakValue: 42.88 }, cost };
    if (path === "/api/cost/access") return { ok: true, canSeeCost: true };
    if (path.startsWith("/api/admin/item-costs/units")) return { ok: true, rules: [], sources: ["fixed", "itemLength", "sheetQty", "pack"], ruleId: 1 };
    if (path.startsWith(`/api/admin/accounts/${MOCK_ACCT}/cost-margin/tier-suggest`)) {
      return { ok: true, source: "rule-based", model: null,
        rationale: "Rule-based: steps at about 2× and 4× their typical order of 3 box(es), 3% and 6% under today's price, held to the 25% margin floor.",
        item: { itemNo: item.itemNo, description: item.description, uom: "BX", boxQty: 1, boxLabel: "100PC/BX", unitIsBox: true, basePriceUsd: 226.4, customerPriceUsd: 129.3, rule: item.rule },
        customer: { custNo: "MOCK1", name: "Mock Account" }, cost: { landedUsd: 76.2426, fobUsd: 68.0738, costBasis: "sheet" }, fx,
        floor: { pct: 25, priceUsd: 101.66 },
        history: { months: 24, orders: 5, totalBoxes: 14, medianBoxesPerOrder: 3, avgBoxesPerOrder: 2.8, largestBoxesPerOrder: 4, monthlyBoxes: 0.58, medianDaysBetweenOrders: 30.5, lastPurchaseDate: "2026-09-30", avgPricePaid: 126.6 },
        existingRules: [],
        tiers: [
          { minBoxes: 1, minQty: 0, priceUsd: 129.3, why: "Today's price", flags: [] },
          { minBoxes: 6, minQty: 6, priceUsd: 125.42, why: "About twice their usual order (3 boxes)", flags: [] },
          { minBoxes: 12, minQty: 12, priceUsd: 121.54, why: "About four times their usual order", flags: [] },
        ] };
    }
    if (path.startsWith(`/api/admin/accounts/${MOCK_ACCT}/cost-margin/item`)) {
      return { ok: true, fx, item: { ...item, stock: { uom: "BX", cbc: { onHand: 42, allocated: 18, available: 24, onOrder: 60 },
        totals: { onHand: 45, allocated: 18, available: 27, onOrder: 60 }, locationCount: 2, openPoLines: 2,
        nextPo: [{ poNo: "P10231", qtyRemaining: 40, promiseDt: "2026-11-14", vendName: "" }, { poNo: "P10255", qtyRemaining: 20, promiseDt: "2026-12-05", vendName: "" }] } } };
    }
    if (path.startsWith(`/api/admin/accounts/${MOCK_ACCT}/cost-margin`)) {
      return { ok: true, fx, since: "2025-10-21", until: "2026-10-21", period: new URLSearchParams(path.split("?")[1] || "").get("period") || "12m", truncated: false, customer: { name: "Mock Account", custNo: "MOCK1" },
        costImport: { importId: 1, quarter: "2026Q1", fileName: "2026Q1-CBC-COST.xls", uploadedAt: new Date().toISOString(), itemCount: 1741, fxNtdPerUsd: 30.5, landedFactor: 1.12 },
        items: [{ ...item, prodCat: "HP", prodCatLabel: "Headrail parts", categoryGroups: ["Horizontal blinds"] }, vane] };
    }
  }

  // Sales plans (UI-only)
  if (path.startsWith("/api/sales/")) {
    const now = new Date().toISOString();
    const planMatch = path.match(/^\/api\/sales\/plans\/(\d+)(?:\/([a-z-]+))?/);
    if (path.startsWith("/api/sales/plans?")) return { ok: true, plans: mockPlans.filter((p) => p.status !== "done" && p.status !== "cancelled") };
    const acct = path.match(/^\/api\/sales\/accounts\/([^/]+)\/(plan-ideas|plan)/);
    if (acct && acct[2] === "plan-ideas") {
      return { ok: true, talkingPoints: ["Ask about the spring apartment turnover"], ideas: [
        { kind: "gap", prodCat: "RS", label: "Roller Shades", value: 4200, text: "Not buying Roller Shades — similar dealers put 18.0% of spend there (≈$4,200/yr at this account's size)" },
        { kind: "drop", prodCat: "VB", label: "Vertical Blinds", value: 1500, text: "Vertical Blinds down $1,500 vs the prior period" },
        { kind: "strong", prodCat: "FW", label: "Faux Wood", value: 18000, text: "Strong in Faux Wood ($18,000) — offer related items" },
      ] };
    }
    if (acct && acct[2] === "plan") {
      const accountId = decodeURIComponent(acct[1]);
      let p = mockPlans.find((x) => x.accountId === accountId && (x.status === "researching" || x.status === "ready"));
      if (m === "POST") {
        if (!p) { p = { id: mockPlanSeq++, accountId, accountName: "Mock Account", status: "researching", rawNotes: "", cleanNotes: "", interests: [], createdAt: now, updatedAt: now }; mockPlans.push(p); }
        return { ok: true, plan: p };
      }
      return { ok: true, plan: p || null, lastDone: null, account: { name: "Mock Account", phone: "555-0100", email: "buyer@example.com", address: "1 Main St, Los Angeles, CA 90001" } };
    }
    if (planMatch) {
      const p = mockPlans.find((x) => x.id === Number(planMatch[1]));
      if (!p) return { ok: false, error: "Plan not found" };
      const action = planMatch[2];
      if (!action) { Object.assign(p, body || {}, { updatedAt: now }); return { ok: true, plan: p }; }
      if (action === "clean") {
        const lines = String(body?.rawNotes || p.rawNotes || "").split(/\n+/).map((l) => l.replace(/^[•\s-]+/, "").trim()).filter(Boolean);
        Object.assign(p, { rawNotes: body?.rawNotes || p.rawNotes, cleanNotes: `Opportunities:\n${lines.map((l) => `• ${l}`).join("\n")}`, interests: [{ label: "Roller Shades", reason: "peers buy them" }], suggestedAction: "visit", suggestedReason: "A showroom visit with samples fits a new product line.", status: "ready", updatedAt: now });
        return { ok: true, plan: p };
      }
      if (action === "draft-email") {
        Object.assign(p, { emailSubject: "Roller shades for your spring projects", emailBody: "Hi,\n\nI noticed you've been busy with faux wood this year — would roller shades help on your upcoming jobs? Happy to drop off samples.\n\nThanks,\nMe", nextAction: "email", updatedAt: now });
        return { ok: true, draft: { subject: p.emailSubject, body: p.emailBody }, plan: p };
      }
      if (action === "complete") { Object.assign(p, { status: "done", nextAction: body?.action, completedAt: now, calendarEventLink: body?.calendarEventLink || null, updatedAt: now }); return { ok: true, plan: p, contactNoteId: 1 }; }
    }
    return { ok: true };
  }

  // Item Lookup (UI-only): two fake items so the compare view can be exercised without Macola
  if (path.startsWith("/api/cs/items/search")) {
    const q = decodeURIComponent((path.split("q=")[1] || "").split("&")[0]).toUpperCase();
    const all = [
      { itemNo: "982-301-9", description: "FAUX WOOD SLAT 2IN WHITE", pack: "100", stocked: true, onHand: 1200, available: 850, inactive: false },
      { itemNo: "982-302-9", description: "FAUX WOOD SLAT 2IN ALABASTER", pack: "100", stocked: true, onHand: 300, available: -20, inactive: false },
      { itemNo: "123-456-7", description: "ROLLER CLUTCH 38MM", pack: "10", stocked: false, onHand: 0, available: 0, inactive: true },
    ];
    return { ok: true, items: all.filter((it) => it.itemNo.includes(q) || it.description.includes(q)) };
  }
  if (path.startsWith("/api/cs/items/")) {
    const itemNo = decodeURIComponent(path.replace("/api/cs/items/", "").split("?")[0]);
    const seed = itemNo.endsWith("2-9") ? 2 : 1;
    return {
      ok: true,
      asOf: "2026-10-12",
      item: { itemNo, description: `MOCK ITEM ${itemNo}`, pack: "100", uom: "EA", prodCat: "FVV", prodCatLabel: "Faux Valance", activityCd: "A", inactive: false, stocked: true, price: 12.5 * seed, invClass: "A" },
      locations: [{ loc: "CBC", onHand: 1200 * seed, allocated: 350, available: 1200 * seed - 350, onOrder: 500, price: 12.5 * seed, invClass: "A", bin: "A-12" }],
      totals: { onHand: 1200 * seed, allocated: 350, available: 1200 * seed - 350, onOrder: 500 },
      purchaseOrders: { rows: [{ poNo: "PO1001", lineNo: 1, vendNo: "V1", vendName: "Taiwan Factory", orderDt: "2026-09-01", poStatus: "O", qtyOrdered: 500, qtyReceived: 0, qtyRemaining: 500, promiseDt: "2026-11-15", requestDt: "2026-11-01", uom: "EA" }], sumRemaining: 500, hasHeader: true, hasVendor: true },
      plannedLots: { lots: [], timing: {}, windowWeeks: 12, estimate: true },
      webOrders: [{ webOrderId: 50001, customerName: "ACME BLINDS", accountNumber: "ACME", status: "pending", createdAt: "2026-10-10T12:00:00Z", macolaOrderNo: "", po: "A-1", qty: 40, lineCount: 1 }],
      recentShipments: [{ shippedDt: "2026-10-08", invDt: "2026-10-08", invNo: "INV1", ordNo: "SO1", custNo: "ACME", custName: "ACME BLINDS", qty: 20, unitPrice: 12.5 * seed }],
      usage: { wk1: 20, wk4: 90, wk8: 180, wk12: 260, wk52: 1100 },
      onOrderMismatch: false,
      includeHidden: false,
      excludedAccounts: ["JUSTFAUX"],
    };
  }

  // Category Groups (UI-only): two overlapping groups so the admin editor, the Sales review
  // and Item Lookup's cross-sell card can be exercised without Macola
  {
    const cgGroups = [
      { groupId: 1, name: "Faux Wood", description: "Slats, readymade and valance", sortOrder: 10, isActive: true, members: ["FS0", "R22", "FVV"] },
      { groupId: 2, name: "Valances", description: "", sortOrder: 20, isActive: true, members: ["FVV", "VAZ"] },
    ];
    const cgLabels = { FS0: "Faux Wood Slats", R22: "Readymade 2\" Faux", FVV: "Faux Valance", VAZ: "Vertical Valance", PVC: "PVC Vanes — Curved" };
    const cgCust = (cusNo, name, byCat, members) => {
      const bought = members.filter((c) => byCat[c] > 0);
      const missing = members.filter((c) => !bought.includes(c));
      const total = bought.reduce((a, c) => a + byCat[c], 0);
      return { cusNo, name, accountId: null, total, byCat, bought, missing, coverage: bought.length / members.length,
        opportunity: missing.length > 0, exactlyOne: bought.length === 1,
        suggestions: missing.map((c) => ({ prodCat: c, label: cgLabels[c] || c, adoption: 0.5 })) };
    };
    const cgReview = (g) => {
      const m = g.members;
      const customers = [
        cgCust("ACME", "ACME BLINDS", { [m[0]]: 18200 }, m),
        cgCust("SUNSET", "SUNSET SHADES", { [m[0]]: 9100, [m[1]]: 2400 }, m),
        cgCust("PACIFIC", "PACIFIC WINDOW CO", Object.fromEntries(m.map((c) => [c, 5000])), m),
      ];
      return { ok: true, group: g, window: { months: 12 }, members: m,
        categories: m.map((c, i) => ({ prodCat: c, label: cgLabels[c] || c, buyers: 3 - i, adoption: (3 - i) / 3, revenue: 10000 - i * 2000 })),
        customers,
        summary: { buyers: 3, fullCoverage: 1, opportunities: 2, exactlyOne: 1, groupSpend: 44700, spendAtStake: 15400 } };
    };
    if (path === "/api/admin/category-groups" || path === "/api/cs/category-groups") return { ok: true, groups: cgGroups };
    if (path.startsWith("/api/cs/category-groups/items/")) {
      const cat = decodeURIComponent(path.split("/items/")[1].split("?")[0]).toUpperCase();
      return { ok: true, prodCat: cat, label: cgLabels[cat] || "", window: { months: 12 }, items: [
        { itemNo: "982-301-9", description: "FAUX WOOD SLAT 2IN WHITE 100", obsolete: false, revenue: 48200, qty: 3900, buyers: 41 },
        { itemNo: "982-302-9", description: "FAUX WOOD SLAT 2IN ALABASTER 100", obsolete: false, revenue: 12900, qty: 1100, buyers: 17 },
        { itemNo: "982-399-9", description: "FAUX WOOD SLAT 2IN OLD OAK", obsolete: true, revenue: 0, qty: 0, buyers: 0 },
      ] };
    }
    if (path === "/api/admin/category-groups/categories" || path.startsWith("/api/cs/category-groups/categories")) {
      return { ok: true, categories: Object.entries(cgLabels).map(([prodCat, label], i) => ({ prodCat, label, items: 40 - i, activeItems: 30 - i, customers: 80 - i * 9, revenue12: 900000 - i * 150000 })) };
    }
    if (path.startsWith("/api/admin/category-groups")) return { ok: true, groupId: 1, created: [] };
    if (path.startsWith("/api/cs/category-groups/review")) {
      const id = Number((path.split("groupId=")[1] || "1").split("&")[0]);
      return cgReview(cgGroups.find((g) => g.groupId === id) || cgGroups[0]);
    }
    if (path.startsWith("/api/cs/category-groups/for-prodcat/")) {
      const cat = decodeURIComponent(path.split("/for-prodcat/")[1].split("?")[0]).toUpperCase();
      const groups = cgGroups.filter((g) => g.members.includes(cat)).map((g) => {
        const r = cgReview(g);
        const gaps = r.customers.filter((c) => c.opportunity && c.bought.includes(cat));
        return { groupId: g.groupId, name: g.name, members: g.members, categories: r.categories, buyersOfCat: 3, gapCount: gaps.length, customers: gaps };
      });
      return { ok: true, prodCat: cat, window: { months: 12 }, groups };
    }
  }

  // Companion Kits + dealer growth review (UI-only)
  if (path.includes("/companion-kits")) {
    const ckKit = { kitId: 1, name: '2" faux head rail kit', notes: "", isActive: true, slots: [
      { label: "Head rail", qtyPerKit: 1, items: ["220-113-205"] },
      { label: "Ladder", qtyPerKit: 100, items: ["900-301-9"] },
      { label: "End caps", qtyPerKit: 4, items: ["220-06A", "220-206U"] },
      { label: "Brackets", qtyPerKit: 4, items: ["220-107SU", "220-207SU"] },
      { label: "Wand", qtyPerKit: 1, items: ["220-202M"] },
    ] };
    const ckItems = {
      "220-113-205": { itemNo: "220-113-205", description: "HEADRAIL 2IN FAUX WHITE", uom: "EA" },
      "900-301-9": { itemNo: "900-301-9", description: "LADDER 2IN WHITE", uom: "FT" },
      "220-06A": { itemNo: "220-06A", description: "END CAP WHITE", uom: "EA" },
      "220-206U": { itemNo: "220-206U", description: "END CAP UNIVERSAL", uom: "EA" },
      "220-107SU": { itemNo: "220-107SU", description: "BRACKET STD", uom: "EA" },
      "220-207SU": { itemNo: "220-207SU", description: "BRACKET HD", uom: "EA" },
      "220-202M": { itemNo: "220-202M", description: "WAND 24IN", uom: "EA" },
    };
    const ckSlot = (i, actual, kits, price) => {
      const s = ckKit.slots[i];
      const expected = kits * s.qtyPerKit;
      const shortfall = Math.max(0, expected - actual);
      return { index: i, label: s.label, items: s.items, qtyPerKit: s.qtyPerKit, actual, byItem: { [s.items[0]]: actual }, impliedKits: actual / s.qtyPerKit,
        expected, shortfall, capture: expected ? Math.min(1, actual / expected) : 1, unitPrice: price, estValue: Math.round(shortfall * price * 100) / 100, anchor: actual / s.qtyPerKit >= kits };
    };
    const ckMeasure = (acts, prices = [12, 0.22, 0.4, 0.6, 1.1]) => {
      const kits = Math.max(...acts.map((a, i) => a / ckKit.slots[i].qtyPerKit));
      const slots = acts.map((a, i) => ckSlot(i, a, kits, prices[i]));
      return { kitsBuilt: kits, anchorIndex: slots.findIndex((s) => s.anchor), capture: slots.reduce((t, s) => t + s.capture, 0) / slots.length,
        estValue: Math.round(slots.reduce((t, s) => t + s.estValue, 0) * 100) / 100, missingCount: slots.filter((s) => s.shortfall > 0).length, slots };
    };
    const ckCustomers = [
      { cusNo: "ACME", name: "ACME BLINDS", accountId: "mock-acme", note: "", ...ckMeasure([40, 900, 160, 160, 40]) },
      { cusNo: "SUNSET", name: "SUNSET SHADES", accountId: "mock-sunset", note: "", ...ckMeasure([12, 1200, 48, 0, 12]) },
      { cusNo: "PACIFIC", name: "PACIFIC WINDOW CO", accountId: null, note: "", ...ckMeasure([20, 2000, 80, 80, 20]) },
    ];
    const ckInsights = [
      { insightId: 1, kitId: 1, kitName: ckKit.name, cusNo: "ACME", customerName: "ACME BLINDS", accountId: "mock-acme", kind: "customer_question", summary: "ACME built ~40 kits but bought only 900 ft of ladder (4,000 ft expected).", question: "Does ACME buy ladder from another supplier, or use a different ladder item?", suggested: null, status: "open", createdAt: new Date().toISOString() },
      { insightId: 2, kitId: 1, kitName: ckKit.name, cusNo: null, kind: "ratio_question", summary: "Customers who buy every line take a median 88 ft of ladder per head rail, not 100.", question: "Change the ladder line to 88 ft per kit?", suggested: [{ index: 1, qtyPerKit: 88 }], status: "open", createdAt: new Date().toISOString() },
      { insightId: 3, kitId: 1, kitName: ckKit.name, cusNo: "SUNSET", customerName: "SUNSET SHADES", accountId: "mock-sunset", kind: "customer_gap", summary: "Sunset Shades buys head rails, ladder and end caps but no brackets: about 48 brackets a year (~$29) likely bought elsewhere.", question: null, suggested: null, status: "open", createdAt: new Date().toISOString() },
    ];
    if (path === "/api/admin/companion-kits") return { ok: true, kits: [ckKit], items: ckItems };
    if (path.startsWith("/api/admin/companion-kits/search-items")) return { ok: true, items: Object.values(ckItems) };
    if (path.startsWith("/api/admin/companion-kits/item-info")) return { ok: true, items: ckItems };
    if (path.startsWith("/api/admin/companion-kits/prefix-items")) {
      const p = decodeURIComponent(path.split("prefix=")[1] || "").toUpperCase();
      const n = Object.keys(ckItems).filter((k) => k.startsWith(p)).length;
      return { ok: true, item: { itemNo: `${p}*`, description: `All ${n} active items starting with ${p}`, uom: "", count: n, prefix: true } };
    }
    if (/\/api\/admin\/companion-kits\/\d+\/ratios/.test(path)) {
      return { ok: true, customers: 7, slots: ckKit.slots.map((s, i) => ({ index: i, label: s.label, items: s.items, configured: s.qtyPerKit, observedMedian: i === 1 ? 88 : s.qtyPerKit })) };
    }
    if (path.startsWith("/api/admin/companion-kits/ai-review/run")) return { ok: true, runId: "Rmock", kits: 1, insights: 3 };
    if (path.startsWith("/api/admin/companion-kits/import")) return { ok: true, created: ["Imported kit"], skipped: [], errors: [] };
    if (path.startsWith("/api/admin/companion-kits")) return { ok: true, kitId: 1 };
    if (path === "/api/cs/companion-kits") return { ok: true, kits: [ckKit] };
    if (path.startsWith("/api/cs/companion-kits/report")) {
      return { ok: true, kit: ckKit, window: { months: 12 }, items: ckItems, customers: ckCustomers,
        summary: { buyers: 3, withGaps: 2, complete: 1, excluded: 0, estValue: ckCustomers.reduce((t, c) => t + (c.missingCount ? c.estValue : 0), 0) } };
    }
    if (path.startsWith("/api/cs/companion-kits/insights")) {
      if (m === "POST") return { ok: true };
      return { ok: true, insights: ckInsights, lastRun: { StartedAt: new Date().toISOString(), Insights: 3 } };
    }
    if (/\/api\/cs\/companion-kits\/customer\/[^/]+\/growth-feedback/.test(path)) {
      const del = path.match(/growth-feedback\/(\d+)$/);
      if (m === "DELETE" && del) {
        const f = mockGrowthFeedback.find((x) => x.id === Number(del[1]));
        if (f) f.deleted = true;
        return { ok: true };
      }
      if (m === "POST") {
        mockGrowthFeedback.forEach((x) => { if (x.title === body?.title) x.deleted = true; });
        const f = { id: mockGrowthFeedback.length + 1, accountId: "mock", reviewId: body?.reviewId || null, title: body?.title, items: body?.items || [],
          verdict: body?.verdict, comment: body?.comment || "", scope: body?.scope || "account", createdBy: "rep@cbc", createdAt: new Date().toISOString() };
        mockGrowthFeedback.push(f);
        return { ok: true, feedback: f };
      }
      return { ok: true, feedback: mockGrowthFeedback.filter((x) => !x.deleted).reverse(), general: [] };
    }
    if (/\/api\/cs\/companion-kits\/customer\/[^/]+\/growth-review/.test(path)) {
      const review = { id: 1, months: 12, model: "mock", createdBy: "rep@cbc", createdAt: new Date().toISOString(),
        summary: "Steady faux-wood dealer (≈$38k last 12 months, +6%). Verticals fell by half and they have never bought roller shades, which most similar faux buyers also take. Their head-rail buying implies far more ladder than they buy from us.",
        opportunities: [
          { title: "Ladder for the head rails they already buy", why: "40 kits' worth of head rails but only 900 ft of 900-301-9 — about 3,100 ft short.", items: ["900-301-9"], estValue: 682, priority: "high" },
          { title: "Win back vertical blinds", why: "Vertical spend dropped from $6,200 to $3,100.", items: ["VB"], estValue: 3100, priority: "medium" },
          { title: "Introduce roller shades", why: "Fits their faux customers; they have never ordered one.", items: ["RS"], estValue: null, priority: "low" },
        ],
        questions: ["Where do you source ladder today?", "What slowed vertical orders this year?"],
        totals: { last12: 38250, prior12: 36080 },
        basis: { correspondence: { months: 6, notes: 3, contacts: 5, dealerNotes: 2, emails: 14 } },
        growthPotential: { annualUpside: 14800, likelyCapturePct: 35, basis: "Ladder shortfall, verticals back to last year's level, and roller shades at what similar faux dealers spend." },
        email: { subject: "Ladder and verticals for your next jobs", body: "Hi,\n\nThanks for the steady faux wood orders this year. Since you're building plenty of 2\" head rails with us, we can ship the matching 900-301-9 ladder on the same order, which saves you a second supplier and freight.\n\nWe'd also love to help on verticals again. Would a quick call this week work to go over your upcoming jobs?\n\nBest,\nRep" },
      };
      return { ok: true, review };
    }
    if (/\/api\/cs\/companion-kits\/customer\/[^/]+\/notes\//.test(path)) return { ok: true };
    if (path.startsWith("/api/cs/companion-kits/customer/")) {
      return { ok: true, cusNo: "ACME", window: { months: 12 }, items: ckItems, insights: ckInsights.filter((i) => i.cusNo === "ACME"),
        kits: [{ kitId: 1, name: ckKit.name, notes: "", slots: ckKit.slots, measure: ckMeasure([40, 900, 160, 160, 40]), customerNote: null }] };
    }
  }

  // Common patterns: return empty collections to avoid UI crashes
 if (path.startsWith("/api/admin")) return { ok: true, items: [], data: [] };
if (path.startsWith("/api/production")) return { ok: true, items: [], data: [] };
if (path.startsWith("/api/customer-service")) return { ok: true, items: [], data: [] };
if (path.startsWith("/api/orders")) return { ok: true, orders: [], items: [], data: [] };
if (path.startsWith("/api/quotes")) return { ok: true, quotes: [], items: [], data: [] };

  // Default safe response
  return { ok: true };
}

// ---- Core fetch with better errors + timeout ----
export async function apiFetch(
  path,
  { method = "GET", body, headers, signal, timeoutMs = 30000 } = {}
) {
  // ✅ UI-only mode: never hit network
  if (UI_ONLY) {
    return mockApi(path, { method, body, headers });
  }

  // support AbortController timeout
  const controller = !signal && timeoutMs > 0 ? new AbortController() : null;
  const tid = controller
    ? setTimeout(() => controller.abort(new Error("Request timeout")), timeoutMs)
    : null;

  // ---- Body handling (supports JSON + string + FormData) ----
  const hasBody = body !== undefined && body !== null;
  const isBodyString = typeof body === "string";
  const isFormData = typeof FormData !== "undefined" && body instanceof FormData;

  const finalBody = !hasBody
    ? undefined
    : isFormData
    ? body // ✅ let browser set multipart boundary
    : isBodyString
    ? body // already a string (maybe JSON). Don't stringify again.
    : JSON.stringify(body);

  // Only set JSON content-type if we're stringifying an object (NOT FormData).
  const contentTypeHeader =
    hasBody && !isBodyString && !isFormData
      ? { "Content-Type": "application/json" }
      : {};

  // Build final headers safely (headers might be undefined)
  const finalHeaders = {
    ...contentTypeHeader,
    ...(headers || {}),
  };

    // ---- Acting customer (impersonation) headers ----
  try {
    const acting = readActingCustomer();

    if (acting?.accountId && acting?.email) {
      finalHeaders["X-Acting-AccountId"] = String(acting.accountId);
      finalHeaders["X-Acting-Email"] = String(acting.email);
    }
  } catch {
    // ignore
  }


  // ---- CSRF header (write methods only) ----
  const m = String(method || "GET").toUpperCase();
  if (["POST", "PUT", "PATCH", "DELETE"].includes(m)) {
    let csrf = readCookie("cbc_csrf");

    // If missing, bootstrap it once (ignore failures)
    if (!csrf) {
      try {
        await fetch(joinUrl(API_BASE, "/api/csrf"), { credentials: "include" });
        csrf = readCookie("cbc_csrf");
      } catch {}
    }

    // ✅ send ONLY the header your server expects
    if (csrf) finalHeaders["X-CSRF-Token"] = csrf;
  }

  // ---- Pricing override header (ONLY if explicitly set to on/off) ----
  // Server should also enforce admin-only in PROD.
  if (PRICING_OVERRIDE === "on" || PRICING_OVERRIDE === "off") {
    finalHeaders["X-Pricing-Override"] = PRICING_OVERRIDE;
  }

  try {
    const res = await fetch(joinUrl(API_BASE, path), {
      method,
      headers: finalHeaders,
      body: finalBody,
      credentials: "include", // ✅ required for cookie auth
      signal: controller ? controller.signal : signal,
    });

    // Try to parse JSON, but also preserve raw text for debugging
    const text = await res.text();
    let data = undefined;
    try {
      data = text ? JSON.parse(text) : undefined;
    } catch {}

    // A 401 from /api/login is a rejected password or an email without web
    // access, not an expired session — let it fall through so the server's
    // message ("Invalid email or password", ...) reaches the login form.
    if (res.status === 401 && path !== "/api/login") {
      clearAuth();

      window.dispatchEvent(new CustomEvent("cbc:session-expired"));

      throw new Error("SESSION_EXPIRED");
    }

    if (!res.ok) {
      // Prefer server error field. Fall back to the raw text only when it's
      // not an HTML error page — otherwise the UI ends up rendering an entire
      // <!DOCTYPE html>... blob in red.
      const ct = String(res.headers.get("content-type") || "").toLowerCase();
      const looksLikeHtml =
        ct.includes("text/html") ||
        /^\s*<(?:!doctype|html\b|head\b|body\b)/i.test(text || "");
      const msg =
        (data && (data.error || data.message)) ||
        (looksLikeHtml ? `Server error (${res.status})` : text) ||
        `HTTP ${res.status}`;
      const err = new Error(msg);
      err.status = res.status;
      err.payload = data ?? text;
      throw err;
    }

    return data ?? {};
  } finally {
    if (tid) clearTimeout(tid);
  }
}

// ---- Nice small API helpers (optional) ----
export async function apiLogin(email, password) {
  return apiFetch("/api/login", { method: "POST", body: { email, password } });
}

export async function apiLogout() {
  return apiFetch("/api/logout", { method: "POST" });
}

export async function apiSetPassword(email, password) {
  return apiFetch("/api/set-password", {
    method: "POST",
    body: { email, password },
  });
}

export async function apiHealth() {
  return apiFetch("/api/health");
}
